/**
 * Which platform the code runs on, and whether development features are on.
 */

export type Platform = "electron" | "web" | "cli";

type RuntimeGlobal = typeof globalThis & {
  process?: {
    versions?: { node?: string };
    env?: Record<string, string | undefined>;
  };
  window?: Window & { electronAPI?: unknown };
};

const runtimeGlobal = globalThis as RuntimeGlobal;

const isNode = typeof runtimeGlobal.process?.versions?.node === "string";
const isBrowser = typeof runtimeGlobal.window !== "undefined";
/** The desktop app's preload script exposes `electronAPI` on the wallet page. */
export const isElectron = isBrowser && typeof runtimeGlobal.window?.electronAPI !== "undefined";

export function detectPlatform(): Platform {
  if (isElectron) {
    return "electron";
  }
  if (isNode && !isBrowser) {
    return "cli";
  }
  return "web";
}

/**
 * Read a Vite env variable. Vite replaces `import.meta.env.X` at build time
 * only where import.meta.env is referenced directly, so this must not go
 * through an alias. Undefined outside Vite.
 */
function getViteEnv(key: string): string | boolean | undefined {
  try {
    const env: Record<string, string | boolean | undefined> | undefined = import.meta.env;
    if (env && key in env) return env[key];
  } catch {
    // import.meta.env unavailable
  }
  return undefined;
}

/** Development mode: Vite's DEV flag, else NODE_ENV other than production. */
export function isDevMode(): boolean {
  const viteDev = getViteEnv("DEV");
  if (typeof viteDev === "boolean") {
    return viteDev;
  }
  if (typeof runtimeGlobal.process?.env?.NODE_ENV === "string") {
    return runtimeGlobal.process.env.NODE_ENV !== "production";
  }
  return true;
}

/** Mock data: VITE_ENABLE_MOCKS or ENABLE_MOCKS when set, else development mode. */
export function shouldUseMockData(): boolean {
  const viteMocks = getViteEnv("VITE_ENABLE_MOCKS");
  if (typeof viteMocks === "string") {
    return viteMocks === "true";
  }
  if (typeof runtimeGlobal.process?.env?.ENABLE_MOCKS === "string") {
    return runtimeGlobal.process.env.ENABLE_MOCKS === "true";
  }
  return isDevMode();
}
